import { createSelector } from "reselect"
import { get, toUpper, find, pick } from "lodash"
import { getIndices } from "selectors/indices"
import { getBenchmarksLookup, getPortfolios } from "selectors/portfolios"
import { getBaskets } from "resources/baskets"

const EMPTY_OBJECT = {}

const getPortfolio = state => get(state, "global.data.portfolio.data")
export const getRawPortfolioBetaInstrument = state => get(state, "global.data.portfolioBetaInstrument.data.portfolio.betaInstrument")
export const getPortfolioBetaInstrumentDescriptors = state => get(state, "global.data.portfolioBetaInstrumentDescriptors.data", EMPTY_OBJECT)

export const getPortfolioBetaInstrumentSecurity = createSelector(
  [getPortfolio],
  (portfolio) => get(portfolio, "betaSecurityId"),
)

export const getPortfolioBetaInstrument = createSelector(
  [getRawPortfolioBetaInstrument, getPortfolioBetaInstrumentDescriptors, getIndices, getPortfolios, getBenchmarksLookup, getBaskets],
  (betaInstrument, descriptors, indices, portfolios, benchmarks, baskets) => {
    const { securityId, resource, benchmarkId, indexId } = betaInstrument ?? {}

    // ETFs
    if (securityId && descriptors) {
      const { name, assetSubclass, ticker, mic, internalId, hasComposition } = descriptors
      const upperSubclass = toUpper(assetSubclass)
      if (upperSubclass !== "ETF") {
        return null
      }
      return {
        type: upperSubclass,
        id: internalId,
        ticker,
        mic,
        name,
        hasComposition,
      }
    }

    // Indices
    if (indexId) {
      const index = find(indices, { id: indexId })
      if (index) {
        return {
          type: "INDEX",
          ...index,
        }
      }
    }

    // Benchmarks
    if (benchmarkId) {
      const benchmark = get(benchmarks, benchmarkId)
      return {
        type: "BENCHMARK",
        ...(benchmark || { id: benchmarkId }),
      }
    }

    // Portfolios and Swaps
    if (resource) {
      const { type, alias } = resource
      if (type === "PORTFOLIO") {
        const portfolio = find(portfolios, { shortId: alias })
        if (portfolio) {
          return {
            type: "PORTFOLIO",
            ...pick(portfolio, ["id", "name", "description"]),
          }
        }
      }
      if (type === "SWAP") {
        const basket = find(baskets, { ticker: alias })
        if (basket) {
          return {
            type: toUpper(type),
            id: basket.ticker,
            description: basket.description,
          }
        }
      }
    }

    return null
  })

export const getIsInvalidBetaInstrumentReason = createSelector(
  [getPortfolioBetaInstrument, getRawPortfolioBetaInstrument, getBenchmarksLookup],
  (betaInstrument, rawBetaInstrument, benchmarks) => {
    const betaInstrumentType = get(betaInstrument, "type")
    const benchmarkId = get(rawBetaInstrument, "benchmarkId")
    if (betaInstrumentType === "ETF" && !betaInstrument.hasComposition) {
      return "The underlying constituents of the selected ETF instrument are not available with this risk model."
    } else if (benchmarkId && !get(benchmarks, benchmarkId)) {
      return "The selected benchmark instrument is not available with this risk model."
    } else if (rawBetaInstrument && !betaInstrument) {
      return "You do not have permission to view the beta instrument"
    }
  },
)
